/**
 * localDB.js — IndexedDB wrapper pour EasyFacture mode offline
 * Stocke : produits, clients, factures, devis, tickets POS, files d'attente sync
 */

const DB_NAME = 'EasyFactureDB';
const DB_VERSION = 2;

// Schéma des stores
const STORES = {
  // ── Core ────────────────────────────────────────────────────────────
  products:        { keyPath: 'id', indexes: ['name', 'barcode', 'code', 'updatedAt'] },
  clients:         { keyPath: 'id', indexes: ['name', 'email', 'updatedAt'] },
  invoices:        { keyPath: 'id', indexes: ['number', 'clientId', 'status', 'date', 'updatedAt'] },
  quotes:          { keyPath: 'id', indexes: ['number', 'clientId', 'status', 'date', 'updatedAt'] },
  pos_sales:       { keyPath: 'id', indexes: ['number', 'clientId', 'date', 'updatedAt'] },
  // ── Nouveaux stores v2 ───────────────────────────────────────────────
  deliveries:      { keyPath: 'id', indexes: ['number', 'clientId', 'status', 'date', 'updatedAt'] },
  returns:         { keyPath: 'id', indexes: ['number', 'clientId', 'status', 'date', 'updatedAt'] },
  suppliers:       { keyPath: 'id', indexes: ['name', 'email', 'updatedAt'] },
  purchase_orders: { keyPath: 'id', indexes: ['number', 'supplierId', 'status', 'orderDate', 'updatedAt'] },
  credits:         { keyPath: 'id', indexes: ['clientId', 'type', 'date', 'createdAt'] },
  // ── Infra ────────────────────────────────────────────────────────────
  sync_queue:      { keyPath: 'queueId', autoIncrement: true, indexes: ['entity', 'operation', 'status', 'createdAt'] },
  meta:            { keyPath: 'key' },
};

let _db = null;

// ─── Ouvrir la base ────────────────────────────────────────────────────────
export function openDB() {
  if (_db) return Promise.resolve(_db);

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db = e.target.result;

      Object.entries(STORES).forEach(([storeName, config]) => {
        if (db.objectStoreNames.contains(storeName)) return;

        const store = db.createObjectStore(storeName, {
          keyPath: config.keyPath,
          autoIncrement: config.autoIncrement || false,
        });

        (config.indexes || []).forEach(idx => {
          store.createIndex(idx, idx, { unique: false });
        });
      });
    };

    req.onsuccess = (e) => { _db = e.target.result; resolve(_db); };
    req.onerror   = (e) => reject(e.target.error);
  });
}

// ─── Helpers génériques ───────────────────────────────────────────────────
function tx(storeName, mode = 'readonly') {
  return _db.transaction(storeName, mode).objectStore(storeName);
}

function promisify(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(e.target.error);
  });
}

// ─── CRUD générique ───────────────────────────────────────────────────────
export async function dbGet(store, key) {
  await openDB();
  return promisify(tx(store).get(key));
}

export async function dbPut(store, record) {
  await openDB();
  return promisify(tx(store, 'readwrite').put(record));
}

export async function dbPutMany(store, records) {
  await openDB();
  const s = tx(store, 'readwrite');
  return Promise.all(records.map(r => promisify(s.put(r))));
}

export async function dbDelete(store, key) {
  await openDB();
  return promisify(tx(store, 'readwrite').delete(key));
}

export async function dbGetAll(store) {
  await openDB();
  return promisify(tx(store).getAll());
}

export async function dbGetByIndex(store, indexName, value) {
  await openDB();
  return promisify(tx(store).index(indexName).getAll(value));
}

export async function dbCount(store) {
  await openDB();
  return promisify(tx(store).count());
}

export async function dbClear(store) {
  await openDB();
  return promisify(tx(store, 'readwrite').clear());
}

// ─── META (config, tokens, lastSync) ─────────────────────────────────────
export async function metaGet(key) {
  const record = await dbGet('meta', key);
  return record?.value ?? null;
}

export async function metaSet(key, value) {
  return dbPut('meta', { key, value, updatedAt: new Date().toISOString() });
}

// ─── SYNC QUEUE ────────────────────────────────────────────────────────────
/**
 * Ajoute une opération à la queue de synchronisation
 * @param {string} entity   - 'invoices' | 'quotes' | 'clients' | 'products' | 'pos_sales'
 * @param {string} operation - 'create' | 'update' | 'delete'
 * @param {object} data      - payload complet
 * @param {string} localId   - ID local (préfixé 'local_')
 */
export async function enqueueSync(entity, operation, data, localId) {
  await openDB();
  return promisify(tx('sync_queue', 'readwrite').add({
    entity,
    operation,
    data,
    localId,
    status: 'pending',    // pending | syncing | done | error
    retries: 0,
    createdAt: new Date().toISOString(),
    error: null,
  }));
}

export async function getPendingQueue() {
  await openDB();
  return promisify(tx('sync_queue').index('status').getAll('pending'));
}

export async function updateQueueItem(queueId, updates) {
  await openDB();
  const item = await dbGet('sync_queue', queueId);
  if (!item) return;
  return dbPut('sync_queue', { ...item, ...updates });
}

export async function clearDoneQueue() {
  await openDB();
  const all = await dbGetAll('sync_queue');
  const done = all.filter(i => i.status === 'done');
  const s = tx('sync_queue', 'readwrite');
  return Promise.all(done.map(i => promisify(s.delete(i.queueId))));
}

// ─── Recherche fulltext simple ────────────────────────────────────────────
export async function searchProducts(query) {
  const all = await dbGetAll('products');
  if (!query) return all;
  const q = query.toLowerCase();
  return all.filter(p =>
    p.name?.toLowerCase().includes(q) ||
    p.code?.toLowerCase().includes(q) ||
    p.barcode?.includes(q)
  );
}

export async function searchClients(query) {
  const all = await dbGetAll('clients');
  if (!query) return all;
  const q = query.toLowerCase();
  return all.filter(c =>
    c.name?.toLowerCase().includes(q) ||
    c.email?.toLowerCase().includes(q) ||
    c.phone?.includes(q)
  );
}

// ─── Génération ID local ──────────────────────────────────────────────────
export function generateLocalId(prefix = 'local') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// ─── Stats rapides ────────────────────────────────────────────────────────
export async function getLocalStats() {
  await openDB();
  const [products, clients, invoices, quotes, sales, pending] = await Promise.all([
    dbCount('products'),
    dbCount('clients'),
    dbCount('invoices'),
    dbCount('quotes'),
    dbCount('pos_sales'),
    getPendingQueue(),
  ]);
  return { products, clients, invoices, quotes, sales, pendingSync: pending.length };
}